import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

interface LegalPageProps {
  titleKey: string;
  lastUpdatedKey: string;
  introKey: string;
  sections: { titleKey: string; textKey: string }[];
  contactKey: string;
  seo: {
    title: string;
    description: string;
    keywords: string;
    canonicalUrl: string;
  };
}

export default function LegalPage({
  titleKey,
  lastUpdatedKey,
  introKey,
  sections,
  contactKey,
  seo,
}: LegalPageProps) {
  const { t } = useTranslation();

  const pageLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `https://debugin.com${seo.canonicalUrl}`,
    'url': `https://debugin.com${seo.canonicalUrl}`,
    'name': seo.title,
    'description': seo.description,
    'isPartOf': {
      '@id': 'https://debugin.com/#website',
    },
    'about': {
      '@id': 'https://debugin.com/#organization',
    },
  };

  return (
    <div className="min-h-screen bg-background-50">
      <title>{seo.title}</title>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(pageLd) }}
      />

      <Navbar />
      <main>
        <article className="pt-24 md:pt-32 pb-16 md:pb-24">
          <div className="w-full px-4 md:px-6 lg:px-10">
            <div className="max-w-3xl mx-auto">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tight text-foreground-950 mb-3">
                {t(titleKey)}
              </h1>
              <p className="text-xs md:text-sm text-foreground-400 mb-10">
                {t(lastUpdatedKey)}
              </p>

              <p className="text-sm md:text-base text-foreground-700 leading-relaxed mb-10">
                {t(introKey)}
              </p>

              {sections.map((section, i) => (
                <section key={i} className="mb-8">
                  <h2 className="text-lg md:text-xl font-bold text-foreground-950 mb-3">
                    {t(section.titleKey)}
                  </h2>
                  <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                    {t(section.textKey)}
                  </p>
                </section>
              ))}

              <div className="mt-12 p-6 bg-background-100 rounded-xl border border-background-200/60">
                <p className="text-sm text-foreground-600 leading-relaxed">
                  {t(contactKey)}
                </p>
              </div>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
}